import { DEFAULT_ANALYSIS_SETTINGS } from '@/constants/default-analysis-settings';
import {
    CameraCreationItem,
    CameraCreationStatus,
    CameraConnectionStatus,
    ConnectionStatus,
} from '@/types/camera';

export const DEFAULT_CAMERA_VALUES: Omit<CameraCreationItem, 'id' | 'status'> =
    {
        ...DEFAULT_ANALYSIS_SETTINGS,
        name: 'Live Camera',
        streamUrl: '',
        username: '',
        password: '',
    };

export const DEFAULT_CONNECTION_STATUS: CameraConnectionStatus = {
    status: ConnectionStatus.IDLE,
};

export const INITIAL_CAMERAS_STATE: CameraCreationItem[] = [
    {
        id: '1',
        ...DEFAULT_CAMERA_VALUES,
        status: CameraCreationStatus.PENDING,
    },
];

export const INITIAL_ACTIVE_CAMERA_ID = INITIAL_CAMERAS_STATE[0].id;

export const INITIAL_CAMERA_VALIDATION: Record<string, boolean> = {
    [INITIAL_CAMERAS_STATE[0].id]: false,
};

export const INITIAL_CONNECTION_STATUS: Record<string, CameraConnectionStatus> =
    {
        [INITIAL_CAMERAS_STATE[0].id]: { ...DEFAULT_CONNECTION_STATUS },
    };

export interface ParsedStreamUrl {
    streamUrl: string;
    username?: string;
    password?: string;
}

// decodeURIComponent throws on malformed percent sequences (e.g. a password
// containing a bare `%`). Fall back to the raw value so user input is never
// rejected mid-typing.
function safeDecodeURIComponent(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

// Strips the rtsp/http/https prefix and pulls out `user:pass@` userinfo when
// present, so a full URL pasted into the Stream URL field auto-fills the
// matching Username/Password fields.
export function parseStreamUrl(raw: string): ParsedStreamUrl {
    const withoutProtocol = raw.replace(/^(rtsp|http|https):\/\//i, '');

    // Only treat `@` as a credentials separator when it appears in the
    // authority part (before the first `/`), not inside the path.
    const authorityEnd = withoutProtocol.indexOf('/');
    const authority =
        authorityEnd === -1
            ? withoutProtocol
            : withoutProtocol.slice(0, authorityEnd);

    const atIndex = authority.indexOf('@');
    if (atIndex === -1) {
        return { streamUrl: withoutProtocol };
    }

    const userInfo = authority.slice(0, atIndex);
    const streamUrl = withoutProtocol.slice(atIndex + 1);
    const colonIndex = userInfo.indexOf(':');

    if (colonIndex === -1) {
        return { streamUrl, username: safeDecodeURIComponent(userInfo) };
    }

    return {
        streamUrl,
        username: safeDecodeURIComponent(userInfo.slice(0, colonIndex)),
        password: safeDecodeURIComponent(userInfo.slice(colonIndex + 1)),
    };
}
